<template>
  <!-- Per-zone weather response for the COMPLETE data view (DetectionZoneData):
       shown for EVERY zone, including an honest "no clear weather link" result.
       Data-driven from /weather_insight/ zones; `curve` is computed by the parent
       from real counts + the daily-temp series. Tripwire-aware via `useCrossings`. -->
  <div class="zwx" :class="{ flat: !mine.length }">
    <div class="zwx-head">
      <span class="zwx-ico"><i class="bi bi-cloud-sun"></i></span>
      <span class="zwx-title">{{ $t('weather.zone.title') }}</span>
      <span class="zwx-band" :class="band" :title="bandTip(band)">{{ bandLabel(band) }}</span>
    </div>
    <p class="zwx-lead">{{ lead }}</p>

    <div class="zwx-body">
      <div v-if="curve && curve.length" class="zwx-curve">
        <div class="zwx-curve-title">{{ $t('weather.zone.byTemp', { unit: unitLabel }) }}</div>
        <div class="zwx-bars">
          <div v-for="b in curve" :key="b.label" class="zwx-col">
            <div class="zwx-bar" :class="b.tone" :style="{ height: (b.h * 0.7) + 'px' }"></div>
            <div class="zwx-lbl">{{ tempLabel(b.label) }}</div>
          </div>
        </div>
      </div>

      <div class="zwx-side">
        <template v-if="mine.length">
          <div v-for="s in mine" :key="s.var" class="zwx-sig">
            <i class="bi" :class="varIcon(s.var)"></i>
            <span class="zwx-sig-txt">{{ zoneShortText(s) }}</span>
            <b :class="s.dir">{{ signedPct(s) }}</b>
          </div>
        </template>
        <div v-else class="zwx-none">
          <i class="bi bi-check2-circle"></i>
          {{ $t('weather.zone.none', { unit: $t(useCrossings ? 'weather.zone.crossingsLower' : 'weather.zone.visitsLower') }) }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import {
  varIcon, signedPct, zoneShortText, bandLabel, bandTip, tempLabel,
} from '@/weather/weatherPresentation'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps({
  zoneName: { type: String, required: true },
  zones: { type: Array, default: () => [] },       // all zone atoms from /weather_insight/
  useCrossings: { type: Boolean, default: false }, // tripwire zones count crossings
  curve: { type: Array, default: null },           // [{label,h,tone}] from bucketVisitsByTemp
})

// Atoms for THIS zone (a zone can have >1 weather variable).
const mine = computed(() =>
  (props.zones || []).filter((z) => z.zone === props.zoneName))
const band = computed(() =>
  mine.value.some((s) => s.band === 'established') ? 'established'
    : mine.value.length ? 'watch' : null)
const unitLabel = computed(() => (props.useCrossings ? t('weather.zone.crossings') : t('weather.zone.visits')))
const lead = computed(() => {
  if (!mine.value.length) return t('weather.zone.leadNone')
  const s = mine.value[0]
  if (s.kind === 'anticipatory') return t('weather.zone.leadAnticipatory')
  return t('weather.zone.leadLed')
})
</script>

<style scoped>
.zwx {
  background: linear-gradient(180deg, #f3faf9, #ffffff 60%);
  border: 1px solid #d9ece9; border-left: 3px solid #1f9d9d;
  border-radius: 12px; padding: 14px 16px 16px;
}
.zwx.flat { background: #fafbfb; border-color: #e7ebea; border-left-color: #c2ccca; }
.zwx-head { display: flex; align-items: center; gap: 8px; }
.zwx-ico {
  width: 30px; height: 30px; border-radius: 8px; background: #fff; color: #178f8f;
  display: flex; align-items: center; justify-content: center; font-size: 1rem;
  box-shadow: 0 1px 3px rgba(20, 60, 55, 0.08);
}
.zwx.flat .zwx-ico { color: #8a9a97; }
.zwx-title { font-weight: 650; color: #12615f; font-size: 0.95rem; }
.zwx.flat .zwx-title { color: #556; }
.zwx-band {
  margin-left: auto; font-size: 0.72rem; padding: 3px 9px; border-radius: 999px;
  display: inline-flex; align-items: center; gap: 4px;
}
.zwx-band.established { background: #e6f4ef; color: #2f7d4f; }
.zwx-band.watch { background: #f3f4f6; color: #6b7280; }
.zwx-band.none { background: #eef1f0; color: #7c8987; }
.zwx-lead { margin: 8px 0 12px; font-size: 0.86rem; color: #385c58; line-height: 1.45; }
.zwx.flat .zwx-lead { color: #6a7876; }

.zwx-body { display: flex; gap: 20px; align-items: flex-end; flex-wrap: wrap; }
.zwx-curve { flex: 0 0 auto; }
.zwx-curve-title { font-size: 0.74rem; color: #6b8582; margin-bottom: 6px; font-weight: 600; }
.zwx-bars { display: flex; align-items: flex-end; gap: 10px; height: 84px; }
.zwx-col { display: flex; flex-direction: column; align-items: center; gap: 4px; }
.zwx-bar { width: 26px; border-radius: 5px 5px 2px 2px; background: #9fd0cb; }
.zwx-bar.cold { background: #7fb0d8; }
.zwx-bar.hot { background: #e0a94a; }
.zwx.flat .zwx-bar, .zwx.flat .zwx-bar.cold, .zwx.flat .zwx-bar.hot { background: #cdd6d4; }
.zwx-lbl { font-size: 0.68rem; color: #6b8582; }

.zwx-side { flex: 1 1 220px; min-width: 200px; display: flex; flex-direction: column; gap: 7px; }
.zwx-sig { display: flex; align-items: center; gap: 7px; font-size: 0.84rem; color: #45605c; }
.zwx-sig .bi { color: #178f8f; }
.zwx-sig-txt { flex: 1 1 auto; }
.zwx-sig b.up { color: #2f7d4f; }
.zwx-sig b.down { color: #9a6a1c; }
.zwx-none {
  font-size: 0.82rem; color: #6a7876; display: flex; align-items: flex-start; gap: 7px;
  background: #f1f4f3; border-radius: 8px; padding: 8px 10px;
}
.zwx-none .bi { color: #8a9a97; margin-top: 1px; }

@media (max-width: 560px) {
  .zwx-body { flex-direction: column; align-items: stretch; gap: 12px; }
  .zwx-side { min-width: 0; flex: 1 1 auto; }
  .zwx-bars { justify-content: flex-start; }
}
</style>
